import type { Story } from '@/@types/story'
import StoryActions from './StoryActions'
import StoryHeader from './StoryHeader'
import StoryMediaSwiper from './StoryMediaSwiper'
import StoryNavigationControls from './StoryNavigationControls'
import StoryProgress from './StoryProgress'
import type { StoryMedia } from './storyMedia'
import useStorySwiperNavigation from './useStorySwiperNavigation'
import useStoryViewerHotkeys from './useStoryViewerHotkeys'
import styles from './PersonalStoriesStage.module.css'
export interface PersonalStoriesStageProps {
	active: boolean
	canPreviousPerson: boolean
	canNextPerson: boolean
	stories: Story[]
	activeStory: Story
	activeStoryIndex: number
	media?: StoryMedia
	hasNextPage: boolean
	interactionDisabled: boolean
	showStoryNavigation?: boolean
	onStoryChange: (storyIndex: number) => void
	onLoadMore: () => Promise<boolean>
	onClose: () => void
	onPreviousPerson: () => void
	onNextPerson: () => void
	onDoubleClickLike: () => void
	onLike: () => void
	onComment: () => void
	onTogglePlayback: () => void
	onToggleMuted: () => void
	onPlaybackBlocked: () => void
	onPlaybackTimeChange: (currentTime: number) => void
	onRetryMedia?: () => Promise<void>
	onMediaLoadingChange: (loading: boolean) => void
}
export default function PersonalStoriesStage({
	active,
	canPreviousPerson,
	canNextPerson,
	stories,
	activeStory,
	activeStoryIndex,
	media,
	hasNextPage,
	interactionDisabled,
	showStoryNavigation = true,
	onStoryChange,
	onLoadMore,
	onClose,
	onPreviousPerson,
	onNextPerson,
	onDoubleClickLike,
	onLike,
	onComment,
	onTogglePlayback,
	onToggleMuted,
	onPlaybackBlocked,
	onPlaybackTimeChange,
	onRetryMedia,
	onMediaLoadingChange,
}: PersonalStoriesStageProps) {
	const {
		handleStorySwiper,
		canPreviousStory,
		canNextStory,
		handlePreviousStory,
		handleNextStory,
	} = useStorySwiperNavigation({
		activeStoryIndex,
		storyCount: stories.length,
		hasNextPage,
		onLoadMore,
		wheelEnabled: active && !interactionDisabled,
	})
	useStoryViewerHotkeys({
		enabled: active && !interactionDisabled,
		canPreviousPerson,
		canNextPerson,
		canPreviousStory,
		canNextStory,
		isVideo: media?.type === 'VIDEO',
		onClose,
		onPreviousPerson,
		onNextPerson,
		onPreviousStory: handlePreviousStory,
		onNextStory: () => void handleNextStory(),
		onTogglePlayback,
	})
	return (
		<section
			className={styles.cluster}
			aria-label={`${activeStory.user.screenName}的日记`}
			aria-hidden={active ? undefined : true}
			inert={active ? undefined : true}
		>
			<StoryHeader story={activeStory} />
			<div className={styles.progressColumn}>
				<StoryProgress storyIndex={activeStoryIndex} storyCount={stories.length} />
			</div>
			<StoryMediaSwiper
				active={active}
				activeStoryIndex={activeStoryIndex}
				stories={stories}
				media={media}
				hasNextPage={hasNextPage}
				interactionDisabled={interactionDisabled}
				onStoryChange={onStoryChange}
				onLoadMore={onLoadMore}
				onDoubleClickLike={onDoubleClickLike}
				onTogglePlayback={onTogglePlayback}
				onToggleMuted={onToggleMuted}
				onPlaybackBlocked={onPlaybackBlocked}
				onPlaybackTimeChange={onPlaybackTimeChange}
				onRetryMedia={onRetryMedia}
				onMediaLoadingChange={onMediaLoadingChange}
				onSwiper={handleStorySwiper}
			/>
			<StoryActions story={activeStory} onLike={onLike} onComment={onComment} />
			{showStoryNavigation && (
				<StoryNavigationControls
					navigation={{ canPreviousStory, canNextStory }}
					interactionDisabled={interactionDisabled}
					onPreviousStory={handlePreviousStory}
					onNextStory={() => void handleNextStory()}
				/>
			)}
		</section>
	)
}
